import type { AuthCompleteResponse } from '@/types/auth'

const API_BASE = '/api/auth'

/**
 * Authentication service client.
 * Communicates with the backend API for WebAuthn ceremony operations.
 */
export const authService = {
  /**
   * Begin the authentication ceremony.
   * Requests a challenge and allowed credentials from the backend.
   */
  async beginAuthentication(): Promise<any> {
    const response = await fetch(`${API_BASE}/begin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to begin authentication' }))
      throw new Error(error.message || 'Failed to begin authentication')
    }

    return response.json()
  },

  /**
   * Complete the authentication ceremony.
   * Sends the WebAuthn assertion response to the backend for verification.
   */
  async completeAuthentication(response: any): Promise<AuthCompleteResponse> {
    const res = await fetch(`${API_BASE}/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(response),
    })
    return res.json()
  },

  /**
   * Begin FIDO2 registration.
   */
  async beginRegistration(username: string): Promise<any> {
    const res = await fetch(`/api/register/begin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username }),
    })
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Failed to begin registration' }))
      throw new Error(error.message || `Failed to begin registration: ${res.statusText}`)
    }
    return res.json()
  },

  /**
   * Complete FIDO2 registration.
   */
  async completeRegistration(response: any, username: string): Promise<{ verified: boolean }> {
    const res = await fetch(`/api/register/complete?username=${encodeURIComponent(username)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(response),
    })
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Failed to complete registration' }))
      throw new Error(error.message || `Failed to complete registration: ${res.statusText}`)
    }
    return res.json()
  },

  /**
   * Check if the user has an active session.
   */
  async checkSession(): Promise<{ authenticated: boolean; user?: { displayName: string } }> {
    try {
      const response = await fetch(`${API_BASE}/session`, {
        credentials: 'include',
      })
      if (response.ok) {
        return response.json()
      }
      return { authenticated: false }
    } catch {
      return { authenticated: false }
    }
  },

  /**
   * End the current session.
   */
  async logout(): Promise<void> {
    await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
    })
  },
}
